import { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from "react-router-dom";
import "./DashboardNav.css";
import { PlayersIcon } from "../../../assets/PlayersIcon";
import { CallsIcon } from "../../../assets/CallsIcon";
import { VideoIcon } from "../../../assets/VideoIcon";
import { AdminIcon } from "../../../assets/AdminIcon";
import { CrmIcon } from "../../../assets/CrmIcon";
import { MessagingIcon } from "../../../assets/MessagingIcon";
import { MESSAGING_ENABLED } from "../../../constants/features";
import { ImportIcon } from "../../../assets/ImportIcon";
import { NoteIcon } from "../../../assets/NoteIcon";
import { ScholarshipsIcon } from "../../../assets/ScholarshipsIcon";
import { ScheduleIcon } from "../../../assets/ScheduleIcon";
import { LogoutIcon } from "../../../assets/LogoutIcon";
import { HelpIcon } from "../../../assets/HelpIcon";
import { GuidedToursModal } from "../../tours/GuidedToursModal";
import { useHasGuidedTours } from "../../tours/useHasGuidedTours";
import { ChecklistIcon } from "../../../assets/ChecklistIcon";
import { GetStartedDialog } from "../../onboarding/GetStartedDialog";
import { useGetStartedChecklist } from "../../onboarding/useGetStartedChecklist";
import { RosterPilotLogo } from "../../../assets/RosterPilotLogo";
import { MergeIcon } from "../../../assets/MergeIcon";
import { TrustedContactsIcon } from "../../../assets/TrustedContactsIcon";
import { useAuth } from "../../../hooks/useAuth";
import { useDuplicateCount } from "../../../hooks/useDuplicateCount";
import { useItineraryRequestCount } from "../../../hooks/useItineraryRequestCount";
import { formatBadgeCount } from "../sections/duplicates/duplicateSelection";
import type { Section } from "../DashboardScreen";
import {
  PERMISSIONS,
  hasPermission,
  type Permission,
} from "../../../utils/permissions";

interface DashboardNavProps {
  onLogout: () => void;
}

/** Which count, if any, an item's badge shows. */
type NavBadge = "duplicates" | "itineraryRequests";

/**
 * What the badge's number MEANS, spoken. A bare digit beside a nav label tells a screen reader
 * user nothing about what is waiting, and these are the app's only two badges.
 */
const badgeLabel = (badge: NavBadge | undefined, count: number): string =>
  badge === "duplicates"
    ? `${count} duplicate groups to resolve`
    : `${count} itinerary requests waiting`;

const baseNavItems: { id: Section; label: string; path: string; Icon: React.ComponentType<{ className?: string }>; adminOnly?: boolean; requiresAdmin?: boolean; requiresMessaging?: boolean; requiresPermission?: Permission; badge?: NavBadge }[] = [
  { id: "players", label: "Players", path: "/players", Icon: PlayersIcon },
  { id: "calls", label: "Calls", path: "/calls", Icon: CallsIcon, requiresPermission: PERMISSIONS.calls },
  { id: "video", label: "Video", path: "/video", Icon: VideoIcon, requiresPermission: PERMISSIONS.video },
  // A destination of its own, deliberately not under the CRM's "Communication" group and not
  // admin-gated: texting a recruit is a coach's job, not an administrator's.
  // Hidden entirely for an organization onboarded without messaging (number quota 0 and no
  // numbers). A section whose every action 503s is worse than no section.
  { id: "messaging", label: "Messaging", path: "/messaging", Icon: MessagingIcon, requiresMessaging: true, requiresPermission: PERMISSIONS.messaging },
  { id: "notes", label: "Latest Notes", path: "/latest-notes", Icon: NoteIcon, requiresPermission: PERMISSIONS.notes },
    // Label only. id, path and the permission key stay put, so Page Access keeps working and
  // the existing /scholarships → /rosters redirect still applies. "Rosters" read oddly once a
  // board could be a 2027 recruiting class rather than a roster.
  { id: "scholarships", label: "Roster Planning", path: "/rosters", Icon: ScholarshipsIcon, requiresPermission: PERMISSIONS.rosters },
  { id: "schedule", label: "Schedule", path: "/schedule", Icon: ScheduleIcon, requiresPermission: PERMISSIONS.schedule },
  // The organization's shared directory of outside coaches and scouts. Open to every coach, like
  // Messaging — building this list together is the whole point, and one only an admin could add to
  // would be the phone-shaped problem it exists to fix.
  { id: "trustedContacts", label: "Trusted Contacts", path: "/trusted-contacts", Icon: TrustedContactsIcon, requiresPermission: PERMISSIONS.trustedContacts },
  // Not admin-gated: any coach who can merge players can resolve duplicates.
  { id: "duplicates", label: "Merge Manager", path: "/duplicates", Icon: MergeIcon, badge: "duplicates", requiresPermission: PERMISSIONS.duplicates },
  // The badge is the pending itinerary-request queue: staff have to build those itineraries by
  // hand, and before this the only way to learn one was waiting was to go looking for it.
  { id: "admin", label: "Admin", path: "/admin", Icon: AdminIcon, adminOnly: true, badge: "itineraryRequests" },
];

/*
  Organization settings had no page worth landing on — it was a card that only linked to Users. The
  nav group replaces it: the destinations are the sub-items, so an admin reaches Users in one click
  instead of two.
*/
const SETTINGS_SUB_ITEMS: { label: string; path: string }[] = [
  { label: "Users", path: "/settings/users" },
  // The organization's own bill. Needs no Page Access key of its own: everything under /settings
  // is already role-gated to admin and system_admin, and a coach cannot reach any of it.
  { label: "Billing", path: "/settings/billing" },
];

const CRM_SUB_ITEMS = [
  { label: "Messages", path: "/crm/communications" },
  { label: "Players", path: "/crm/contacts?type=ProspectProfile" },
  { label: "Coaches", path: "/crm/contacts?type=Coach" },
  { label: "X Followers", path: "/crm/twitter/followers" },
  { label: "Parents", path: "/crm/contacts?type=Parent" },
  { label: "Schools", path: "/crm/teams" },
  { label: "Pending Submissions", path: "/crm/submissions/pending" },
  { label: "Connectors", path: "/crm/connectors" },
  { label: "X Post Templates", path: "/crm/settings/x-post-templates" },
];

export const DashboardNav = ({
  onLogout,
}: DashboardNavProps) => {
  const { isSystemAdmin, user } = useAuth();
  const { count: duplicateCount } = useDuplicateCount();
  // system_admin only — the count endpoint refuses anyone else, and the Admin item they would see
  // it on is system-admin-only too.
  const { count: itineraryRequestCount } = useItineraryRequestCount(isSystemAdmin);
  const location = useLocation();
  const isCrmActive = location.pathname.startsWith("/crm");
  const isSettingsActive = location.pathname.startsWith("/settings");
  const [collapsed, setCollapsed] = useState(false);
  const [crmExpanded, setCrmExpanded] = useState(isCrmActive);
  // Seeded open when the current page is already inside settings, so a reload does not hide the
  // sub-item that is highlighted.
  const [settingsExpanded, setSettingsExpanded] = useState(isSettingsActive);
  // Owned here rather than by each layout, so every shell that renders the nav gets the list.
  const [guidedToursOpen, setGuidedToursOpen] = useState(false);
  // Stable, because useDialogFocus re-binds its key listener whenever onClose changes.
  const closeGuidedTours = useCallback(() => setGuidedToursOpen(false), []);
  // Hidden rather than opening on an empty list. Tours ship off, so most coaches have none.
  const showGuidedTours = useHasGuidedTours();
  const [getStartedOpen, setGetStartedOpen] = useState(false);
  const closeGetStarted = useCallback(() => setGetStartedOpen(false), []);
  /*
    The feature probe for Get Started. Without `includeItems` on purpose: this shares the cached read
    the Players page panel makes, so the sidebar costs no extra request on the landing route, and the
    dialog asks for the full list only when it is opened.

    Hidden while loading, so the item never appears and then vanishes, and when the API has no
    checklist (a 404 from an older API). A failed read still shows it: the dialog reads again on open
    and says plainly if it still cannot load, which beats hiding the way in over a blip.
  */
  const { status: getStartedStatus } = useGetStartedChecklist();
  const showGetStarted = getStartedStatus === "ready" || getStartedStatus === "error";

  const isAdmin = user?.profile?.role === 'admin' || isSystemAdmin;

  /** The number a nav item's badge shows, or 0 for an item that carries none. */
  const badgeFor = (badge: NavBadge | undefined): number => {
    if (badge === "duplicates") return duplicateCount;
    if (badge === "itineraryRequests") return itineraryRequestCount;
    return 0;
  };

  /*
    Two gates, and both have to pass. MESSAGING_ENABLED is the build switch deciding whether the
    feature exists in this client at all; the profile flag says whether this organization is paying
    for it. Undefined on the profile means an older payload — treat that as enabled so a missing
    field never hides a section the organization is paying for.
  */
  const messagingEnabled =
    MESSAGING_ENABLED && user?.profile?.messagingEnabled !== false;

  const navItems = baseNavItems.filter(item =>
    (!item.adminOnly || isSystemAdmin) &&
    (!item.requiresAdmin || isAdmin) &&
    (!item.requiresMessaging || messagingEnabled) &&
    (!item.requiresPermission || hasPermission(user?.profile, item.requiresPermission))
  );

  // System admins only. The CRM is platform tooling rather than something an organization
  // administers for itself, so an org admin does not get it — matching the API, where every
  // Crm*Controller is [Authorize(Roles = "system_admin")].
  const showCrm = isSystemAdmin;
  const showSettings = isAdmin;

  // Auto-expand when navigating into CRM pages
  useEffect(() => {
    if (isCrmActive) {
      setCrmExpanded(true);
    }
  }, [isCrmActive]);

  // Same for settings, so a deep link into a user's page opens the group holding it.
  useEffect(() => {
    if (isSettingsActive) {
      setSettingsExpanded(true);
    }
  }, [isSettingsActive]);

  return (
    <nav className={`dashboard-nav ${collapsed ? 'dashboard-nav--collapsed' : ''}`}>
      <div className="dashboard-nav__header">
        {!collapsed && <RosterPilotLogo />}
        <button
          className="dashboard-nav__collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className={`dashboard-nav__collapse-icon ${collapsed ? 'dashboard-nav__collapse-icon--collapsed' : ''}`}>
            ‹
          </span>
        </button>
      </div>

      <div className="dashboard-nav__items">
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            className={({ isActive }) =>
              `dashboard-nav__item ${isActive ? "dashboard-nav__item--active" : ""}`
            }
            title={collapsed ? item.label : undefined}
          >
            <item.Icon className="dashboard-nav__icon" />
            {!collapsed && <span className="dashboard-nav__label">{item.label}</span>}
            {/* Hidden at zero, capped at 99+ — mirrors mobile's CountBadge. */}
            {badgeFor(item.badge) > 0 && (
              <span
                className="dashboard-nav__badge"
                aria-label={badgeLabel(item.badge, badgeFor(item.badge))}
              >
                {formatBadgeCount(badgeFor(item.badge))}
              </span>
            )}
          </NavLink>
        ))}

        {showCrm && (
          <div className="dashboard-nav__group">
            <button
              className={`dashboard-nav__item dashboard-nav__group-toggle ${isCrmActive ? "dashboard-nav__item--active" : ""}`}
              onClick={() => setCrmExpanded(!crmExpanded)}
              title={collapsed ? 'Communication' : undefined}
              // A disclosure button with no aria-expanded does not announce whether it is open.
              aria-expanded={crmExpanded}
            >
              <CrmIcon className="dashboard-nav__icon" />
              {!collapsed && (
                <>
                  <span className="dashboard-nav__label">Communication</span>
                  <span className={`dashboard-nav__chevron ${crmExpanded ? "dashboard-nav__chevron--open" : ""}`}>›</span>
                </>
              )}
            </button>
            {!collapsed && (
              <div className={`dashboard-nav__sub-items${crmExpanded ? " dashboard-nav__sub-items--open" : ""}`}>
                {CRM_SUB_ITEMS.map((sub) => {
                  const subUrl = new URL(sub.path, window.location.origin);

                  // Exact pathname + search match
                  let isActive =
                    subUrl.pathname === location.pathname &&
                    (subUrl.search ? location.search === subUrl.search : true);

                  // On a contact detail page, keep the originating type highlighted
                  if (!isActive && /^\/crm\/contacts\/.+/.test(location.pathname)) {
                    const stateType = (location.state as { contactType?: string } | null)?.contactType;
                    const subType = new URLSearchParams(subUrl.search).get("type");
                    if (stateType && subType) isActive = stateType === subType;
                  }

                  return (
                    <NavLink
                      key={sub.path}
                      to={sub.path}
                      className={`dashboard-nav__sub-item ${isActive ? "dashboard-nav__sub-item--active" : ""}`}
                    >
                      {sub.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {showSettings && (
          <div className="dashboard-nav__group">
            <button
              className={`dashboard-nav__item dashboard-nav__group-toggle ${isSettingsActive ? "dashboard-nav__item--active" : ""}`}
              onClick={() => setSettingsExpanded(!settingsExpanded)}
              title={collapsed ? 'Organization Settings' : undefined}
              aria-expanded={settingsExpanded}
            >
              <ImportIcon className="dashboard-nav__icon" />
              {!collapsed && (
                <>
                  <span className="dashboard-nav__label">Settings</span>
                  <span className={`dashboard-nav__chevron ${settingsExpanded ? "dashboard-nav__chevron--open" : ""}`}>›</span>
                </>
              )}
            </button>
            {!collapsed && (
              <div className={`dashboard-nav__sub-items${settingsExpanded ? " dashboard-nav__sub-items--open" : ""}`}>
                {SETTINGS_SUB_ITEMS.map((sub) => (
                  <NavLink
                    key={sub.path}
                    to={sub.path}
                    className={({ isActive }) =>
                      `dashboard-nav__sub-item ${isActive ? "dashboard-nav__sub-item--active" : ""}`
                    }
                  >
                    {sub.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="dashboard-nav__footer">
        {showGetStarted && (
          <button
            type="button"
            className="dashboard-nav__item dashboard-nav__tours"
            onClick={() => setGetStartedOpen(true)}
            title={collapsed ? 'Get Started' : undefined}
            aria-haspopup="dialog"
          >
            <ChecklistIcon className="dashboard-nav__icon" />
            {!collapsed && <span className="dashboard-nav__label">Get Started</span>}
          </button>
        )}
        {showGuidedTours && (
          <button
            type="button"
            className="dashboard-nav__item dashboard-nav__tours"
            onClick={() => setGuidedToursOpen(true)}
            title={collapsed ? 'Guided tours' : undefined}
            aria-haspopup="dialog"
          >
            <HelpIcon className="dashboard-nav__icon" />
            {!collapsed && <span className="dashboard-nav__label">Guided tours</span>}
          </button>
        )}
        <button
          className="dashboard-nav__logout"
          onClick={onLogout}
          title={collapsed ? 'Logout' : undefined}
        >
          <LogoutIcon className="dashboard-nav__icon" />
          {!collapsed && <span className="dashboard-nav__label">Logout</span>}
        </button>
      </div>

      <GetStartedDialog isOpen={getStartedOpen} onClose={closeGetStarted} />
      <GuidedToursModal isOpen={guidedToursOpen} onClose={closeGuidedTours} />
    </nav>
  );
};
